import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnChanges, SimpleChanges, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { ColumnDefinition, ListItemClickEvent } from '../../../models/columnDefinition';

@Component({
  selector: 'app-generic-list',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatSortModule, MatPaginatorModule, MatIconModule, MatButtonModule, MatTooltipModule, MatInputModule, MatFormFieldModule, MatSelectModule, MatCheckboxModule, FormsModule],
  templateUrl: './generic-list.component.html',
  styleUrls: ['./generic-list.component.scss']
})
export class GenericListComponent<T> implements OnChanges, AfterViewInit {
  dataSource = new MatTableDataSource<T>([]);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;

  @Input() items: T[] | null = [];
  @Input() columnDefinitions: ColumnDefinition[] = [];
  @Input() emptyMessage: string = 'No items to display';
  @Input() pageSize: number = 10;
  @Input() currentPage?: number; // Current page index (0-based) for server-side pagination
  @Input() pageSizeOptions: number[] = [5, 10, 25, 100];
  @Input() enablePaging: boolean = true;
  @Input() enableEdit: boolean = false;
  @Input() enableDelete: boolean = false;
  @Input() enableLink: boolean = false;
  @Input() linkTooltip: string = '';
  @Input() enableView: boolean = false;
  @Input() enableAuditLog: boolean = false; // Enable a per-row "view audit log" action (rendered first)
  @Input() totalItems?: number; // For server-side pagination
  @Input() serverSidePagination: boolean = false; // Enable server-side pagination mode
  @Input() enableTransfer: boolean = false; // Enable transfer action button
  @Input() transferDisabledField: string = ''; // Field name on item; if truthy, transfer button is disabled
  @Input() enableMatching: boolean = false; // Enable matching functionality for merging
  @Input() enableSelection: boolean = false; // Enable per-row selection checkboxes (for bulk actions)
  @Input() sortActive: string = ''; // Default sort column
  @Input() sortDirection: 'asc' | 'desc' | '' = ''; // Default sort direction
  @Input() rowHighlightPredicate?: (item: T) => boolean;
  /**
   * Optional per-row left-edge accent colour. Return a CSS colour to draw a coloured bar on the
   * row's leading edge, or null/undefined for none. Rows that return the same colour read as a
   * connected vertical band — used to visually link related rows (e.g. one audit change set).
   */
  @Input() rowAccentFn?: (item: T) => string | null | undefined;

  @Output() itemClick = new EventEmitter<T>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() editClick = new EventEmitter<T>();
  @Output() deleteClick = new EventEmitter<T>();
  @Output() auditLogClick = new EventEmitter<T>();
  @Output() linkClick = new EventEmitter<T>();
  @Output() viewClick = new EventEmitter<T>();
  @Output() itemSave = new EventEmitter<{item: T, newValues: {[key: string]: any}}>();
  @Output() transferClick = new EventEmitter<T>();
  @Output() readyToMatch = new EventEmitter<{rightItem?: T, matchedItems?: T[]}>();
  @Output() listItemClick = new EventEmitter<ListItemClickEvent<T>>();
  @Output() selectionChange = new EventEmitter<T[]>();

  // Edit state management
  editingItemId: any = null;
  editingValues: {[columnKey: string]: any} = {};

  /** Tooltip shown on the delete button of a protected (system-reserved) row. */
  readonly protectedDeleteTooltip = 'This entry is protected and cannot be deleted';

  // Row selection state management for matching
  matchedItems: T[] = [];
  rightItem: T | null = null;

  // Bulk-action selection state (separate from matching above)
  private selectedIds = new Set<any>();

  constructor(private cdr: ChangeDetectorRef) {
    this.dataSource.data = this.items ?? [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update data source when items change
    if (changes['items']) {
      this.dataSource.data = this.items ?? [];

      // Clear selection on reload/page change so callers never act on stale rows.
      if (this.enableSelection && this.selectedIds.size > 0) {
        this.selectedIds.clear();
        this.emitSelection();
      }
      
      // For server-side pagination: when items arrive, the paginator is now rendered
      // (it's hidden when items array is empty due to template @if condition)
      if (this.serverSidePagination && (this.items ?? []).length > 0) {
        setTimeout(() => {
          if (this.paginator) {
            if (this.totalItems !== undefined) {
              this.paginator.length = this.totalItems;
            }
            if (this.currentPage !== undefined) {
              this.updatePaginatorIndex();
            }
          }
        }, 0);
      }
      
      // For client-side pagination
      if (!this.serverSidePagination) {
        this.reconnectPaginator();
      }
    }

    // Update paginator for server-side pagination when it's already available
    if (this.serverSidePagination && this.paginator) {
      let needsPageIndexUpdate = false;

      if (changes['totalItems']) {
        this.paginator.length = this.totalItems ?? 0;
        needsPageIndexUpdate = true;
      }

      if (changes['currentPage'] && this.currentPage !== undefined) {
        needsPageIndexUpdate = true;
      }

      if (needsPageIndexUpdate && this.currentPage !== undefined) {
        this.updatePaginatorIndex();
      }
    }
  }

  private updatePaginatorIndex(): void {
    if (!this.paginator || this.currentPage === undefined) {
      return;
    }

    // Only update if pageIndex differs from input
    if (this.paginator.pageIndex !== this.currentPage) {
      this.paginator.pageIndex = this.currentPage;
      this.cdr.detectChanges();
      
      // Verify the change persisted
      setTimeout(() => {
        if (this.paginator && this.paginator.pageIndex !== this.currentPage) {
          this.paginator.pageIndex = this.currentPage!;
          this.cdr.detectChanges();
        }
      }, 100);
    }
  }

  ngAfterViewInit() {
    // Connect sort/paginator to dataSource for client-side handling
    if (!this.serverSidePagination) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
      this.reconnectPaginator();
    }

    // Custom sorting accessor for complex data types
    this.dataSource.sortingDataAccessor = (item: T, propertyName: string) => {
      const columnDef = this.columnDefinitions.find(c => c.propertyName === propertyName);
      const value = (item as any)[propertyName];

      if (columnDef?.dataType === 'object' && value) {
        return value.name;
      }

      if (columnDef?.dataType === 'enum' && columnDef.enumType) {
        return columnDef.enumType[value] || '';
      }

      if (columnDef?.dataType === 'badge') {
        return columnDef.badgeMap?.[String(value)]?.label ?? value ?? '';
      }

      return value;
    };

    // For server-side pagination, ensure paginator is initialized
    // Note: Paginator may not exist if items array is empty (due to template @if condition)
    if (this.serverSidePagination) {
      setTimeout(() => {
        if (this.paginator) {
          if (this.totalItems !== undefined) {
            this.paginator.length = this.totalItems;
          }
          if (this.currentPage !== undefined) {
            this.updatePaginatorIndex();
          }
        }
      }, 0);
    }
  }

  private reconnectPaginator(): void {
    // Use setTimeout to ensure the paginator is rendered in the DOM
    setTimeout(() => {
      if (this.paginator) {
        // Force reconnection by temporarily disconnecting and reconnecting
        this.dataSource.paginator = null;
        this.dataSource.paginator = this.paginator;

        // Reset to first page if we have data
        if (this.dataSource.data.length > 0) {
          this.paginator.firstPage();
        }
      } else if (this.dataSource.data.length > 0) {
        // If paginator is not yet available but we have data, try again after a longer delay
        setTimeout(() => this.reconnectPaginator(), 100);
      }
    }, 0);
  }

  onItemClick(item: T): void {
    this.itemClick.emit(item);
  }

  onMatSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
  }

  onPageChangeEvent(event: PageEvent): void {
    this.pageChange.emit(event);
  }

  // Computed property to get just the property names for the table
  get displayedColumns(): string[] {
    const columns = this.columnDefinitions.map(col => col.propertyName);
    if (this.enableSelection) {
      columns.unshift('select');
    }
    if (this.enableEdit || this.enableDelete || this.enableLink || this.enableView || this.enableTransfer || this.enableMatching || this.enableAuditLog) {
      columns.push('actions');
    }
    return columns;
  }

  // ---- Bulk selection helpers ----

  isSelected(item: T): boolean {
    return this.selectedIds.has(this.getItemId(item));
  }

  toggleSelection(item: T, event?: Event): void {
    event?.stopPropagation();
    const id = this.getItemId(item);
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.emitSelection();
  }

  // True only when every row currently in view is selected.
  isAllSelected(): boolean {
    const rows = this.dataSource.data;
    if (rows.length === 0) return false;
    return rows.every(item => this.selectedIds.has(this.getItemId(item)));
  }

  // True when some but not all rows in view are selected.
  isPartiallySelected(): boolean {
    const rows = this.dataSource.data;
    if (rows.length === 0) return false;
    const selectedCount = rows.filter(item => this.selectedIds.has(this.getItemId(item))).length;
    return selectedCount > 0 && selectedCount < rows.length;
  }

  toggleAllSelection(): void {
    const rows = this.dataSource.data;
    if (this.isAllSelected()) {
      rows.forEach(item => this.selectedIds.delete(this.getItemId(item)));
    } else {
      rows.forEach(item => this.selectedIds.add(this.getItemId(item)));
    }
    this.emitSelection();
  }

  // Public method to clear selection - call after a successful bulk action
  public clearSelection(): void {
    this.selectedIds.clear();
    this.emitSelection();
  }

  private emitSelection(): void {
    const idsInView = new Set<any>();
    this.dataSource.data.forEach(item => idsInView.add(this.getItemId(item)));
    const selected = this.dataSource.data.filter(item => this.selectedIds.has(this.getItemId(item)));
    this.selectionChange.emit(selected);
  }

  // Helper method to get display name for a column
  getColumnDisplayName(propertyName: string): string {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.displayName ?? propertyName;
  }

  // Helper method to check if a column is boolean
  isBooleanColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'boolean';
  }

  // Helper method to check if a column is date
  isDateColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'date';
  }

  // Helper method to check if a column is datetime
  isDateTimeColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'datetime';
  }

  // Helper method to check if a column is object
  isObjectColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'object';
  }

  // Helper method to check if a column is enum
  isEnumColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'enum';
  }

  // Helper method to check if a column is array
  isArrayColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'array';
  }

  // Helper method to check if a column is a "first + N more" summary of an array of objects
  isSummaryColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'summary';
  }

  // Whether the column header should be sortable (default true unless explicitly disabled).
  isSortable(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.sortable !== false;
  }

  // Helper method to check if a column renders an info icon with tooltip
  isTooltipIconColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'tooltipIcon';
  }

  isBadgeColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'badge';
  }

  getBadgeLabel(item: T, propertyName: string): string {
    const def = this.columnDefinitions.find(col => col.propertyName === propertyName);
    const raw = (item as any)?.[propertyName];
    if (raw == null) return '';
    return def?.badgeMap?.[String(raw)]?.label ?? String(raw);
  }

  getBadgeClass(item: T, propertyName: string): string {
    const def = this.columnDefinitions.find(col => col.propertyName === propertyName);
    const raw = (item as any)?.[propertyName];
    if (raw == null) return '';
    return def?.badgeMap?.[String(raw)]?.cssClass ?? '';
  }

  // Extra CSS classes for a cell, read from the field named by the column's cellClassKey.
  getCellClasses(item: T, propertyName: string): string {
    const key = this.columnDefinitions.find(col => col.propertyName === propertyName)?.cellClassKey;
    if (!key) return '';
    const value = (item as any)?.[key];
    return typeof value === 'string' ? value : '';
  }

  // Tooltip for a cell, read from the field named by the column's cellTooltipKey.
  getCellTooltip(item: T, propertyName: string): string {
    const key = this.columnDefinitions.find(col => col.propertyName === propertyName)?.cellTooltipKey;
    if (!key) return '';
    const value = (item as any)?.[key];
    return typeof value === 'string' ? value : '';
  }

  // Tooltip text for a tooltip-icon column cell, or null if no icon should be shown.
  getTooltipIconText(item: T, propertyName: string): string | null {
    const value = (item as any)?.[propertyName];
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  private getSummaryLabels(item: T, propertyName: string): string[] {
    const columnDef = this.columnDefinitions.find(col => col.propertyName === propertyName);
    const value = (item as any)[propertyName];
    if (!value || !Array.isArray(value)) return [];

    const labelKey = columnDef?.labelKey ?? 'name';
    const labels = value
      .map(entry => {
        if (entry == null) return '';
        if (typeof entry === 'string') return entry;
        if (typeof entry === 'number' || typeof entry === 'boolean') return String(entry);
        return entry?.[labelKey] ?? '';
      })
      .filter((label: string) => label.length > 0);

    const direction = columnDef?.labelSort ?? 'asc';
    if (direction === 'none') return labels;

    const sorted = labels.sort((a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
    return direction === 'desc' ? sorted.reverse() : sorted;
  }

  // Display: "First +N more" when more than one, else just the first.
  getSummaryDisplay(item: T, propertyName: string): string {
    const labels = this.getSummaryLabels(item, propertyName);
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    return `${labels[0]} +${labels.length - 1} more`;
  }

  // Tooltip: full comma-separated list.
  getSummaryTooltip(item: T, propertyName: string): string {
    return this.getSummaryLabels(item, propertyName).join(', ');
  }

  // Helper method to check if a column is list
  isListColumn(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.dataType === 'list';
  }

  // Helper method to check if a list column is clickable
  isListClickable(propertyName: string): boolean {
    return this.columnDefinitions.find(col => col.propertyName === propertyName)?.listClickable === true;
  }

  // Helper method to get list values for display
  getListValues(item: T, propertyName: string): any[] {
    const value = (item as any)[propertyName];
    if (!value || !Array.isArray(value)) return [];
    return value;
  }

  // Helper method to get the display label for a list item
  getListItemLabel(listItem: any): string {
    if (typeof listItem === 'string') return listItem;
    return listItem?.label || '';
  }

  // Handle list item click
  onListItemClick(row: T, column: string, listItem: any, event: Event): void {
    event.stopPropagation();
    this.listItemClick.emit({ row, column, listItem });
  }

  // Helper method to get enum text value
  getEnumValue(item: T, propertyName: string): string {
    const columnDef = this.columnDefinitions.find(col => col.propertyName === propertyName);
    if (!columnDef || !columnDef.enumType) return '';

    const value = (item as any)[propertyName];
    if (value === null || value === undefined) return '';

    return columnDef.enumType[value] || '';
  }

  // Helper method to get the name property from an object
  getObjectName(item: T, propertyName: string): string {
    const value = (item as any)[propertyName];
    if (!value || typeof value !== 'object') return '';
    return value.name || '';
  }

  // Helper method to get the boolean value for display
  getBooleanValue(item: T, propertyName: string): boolean {
    return Boolean((item as any)[propertyName]);
  }

  // Helper method to get truncated array value for display
  getTruncatedArrayValue(item: T, propertyName: string): string {
    const columnDef = this.columnDefinitions.find(col => col.propertyName === propertyName);
    const value = (item as any)[propertyName];

    if (!value || !Array.isArray(value)) return '';

    const truncateLength = columnDef?.truncateLength || 3;
    const displayItems = value.slice(0, truncateLength);
    const remaining = value.length - truncateLength;

    if (remaining > 0) {
      return displayItems.join(', ') + '...';
    }

    return value.join(', ');
  }

  // Helper method to get full array value for tooltip
  getFullArrayValue(item: T, propertyName: string): string {
    const value = (item as any)[propertyName];
    if (!value || !Array.isArray(value)) return '';
    return value.join(', ');
  }

  // Helper method to format date values (DD/MM/YYYY)
  getFormattedDate(item: T, propertyName: string): string {
    const value = (item as any)[propertyName];
    if (!value) return '';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  // Helper method to format datetime values (DD/MM/YYYY HH:mm:ss)
  getFormattedDateTime(item: T, propertyName: string): string {
    const value = (item as any)[propertyName];
    if (!value) return '';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  // Edit functionality methods
  onEditClick(item: T, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.editClick.emit(item);
    this.startEdit(item);
  }

  onDeleteClick(item: T, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isDeleteProtected(item)) return; // system-reserved rows cannot be deleted
    this.deleteClick.emit(item);
  }

  // A system-reserved row is referenced by program logic and must not be deleted.
  isDeleteProtected(item: T): boolean {
    return Boolean((item as any)?.systemReserved);
  }

  onLinkClick(item: T, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.linkClick.emit(item);
  }

  onViewClick(item: T, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.viewClick.emit(item);
  }

  onAuditLogClick(item: T, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.auditLogClick.emit(item);
  }

  onTransferClick(item: T, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.transferClick.emit(item);
  }

  isTransferDisabled(item: T): boolean {
    if (!this.transferDisabledField) return false;
    return Boolean((item as any)[this.transferDisabledField]);
  }

  startEdit(item: T): void {
    this.editingItemId = this.getItemId(item);
    this.editingValues = {};

    // Initialize editing values with current item values
    this.columnDefinitions.forEach(col => {
      this.editingValues[col.propertyName] = (item as any)[col.propertyName];
    });

    // Focus on the first editable field after a short delay
    setTimeout(() => {
      if (this.editInput) {
        this.editInput.nativeElement.focus();
      }
    }, 100);
  }

  saveEdit(item: T): void {
    this.itemSave.emit({
      item: item,
      newValues: { ...this.editingValues }
    });
    this.cancelEdit();
  }

  cancelEdit(): void {
    this.editingItemId = null;
    this.editingValues = {};
  }

  isEditing(item: T): boolean {
    return this.editingItemId === this.getItemId(item);
  }

  private getItemId(item: T): any {
    return (item as any).id || (item as any).name || JSON.stringify(item);
  }

  onKeyDown(event: KeyboardEvent, item: T): void {
    if (event.key === 'Enter') {
      this.saveEdit(item);
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  hasValidEditingValues(): boolean {
    // Check if all required fields have values
    return this.columnDefinitions.every(col => {
      const value = this.editingValues[col.propertyName];
      return value !== null && value !== undefined && value !== '';
    });
  }

  isEditableColumn(propertyName: string): boolean {
    const columnDef = this.columnDefinitions.find(col => col.propertyName === propertyName);
    return columnDef?.dataType === 'string' || columnDef?.dataType === 'number';
  }

  // Matching functionality methods

  // Get the current state of an item
  getItemState(item: T): 'normal' | 'matched' | 'right' {
    if (!this.enableMatching) {
      return 'normal';
    }

    if (this.rightItem === item) {
      return 'right';
    }
    if (this.matchedItems.includes(item)) {
      return 'matched';
    }
    return 'normal';
  }

  // Handle Match button click
  onMatchClick(item: T, event: Event): void {
    if (!this.enableMatching) {
      return;
    }

    event.stopPropagation(); // Prevent row click

    // If item is already matched, remove it from matchedItems
    if (this.matchedItems.includes(item)) {
      this.matchedItems = this.matchedItems.filter(matchedItem => matchedItem !== item);
    } else {
      // If item is the rightItem, remove it from rightItem first
      if (this.rightItem === item) {
        this.rightItem = null;
      }
      // Add to matchedItems
      this.matchedItems.push(item);
    }

    this.checkReadyToMatch();
  }

  // Handle Right button click
  onRightClick(item: T, event: Event): void {
    if (!this.enableMatching) {
      return;
    }

    event.stopPropagation(); // Prevent row click

    // If item is already the rightItem, remove it
    if (this.rightItem === item) {
      this.rightItem = null;
    } else {
      // Remove from matchedItems if it's there
      this.matchedItems = this.matchedItems.filter(matchedItem => matchedItem !== item);
      // Set as rightItem
      this.rightItem = item;
    }

    this.checkReadyToMatch();
  }

  // Check if we should emit readyToMatch event
  private checkReadyToMatch(): void {
    if (!this.enableMatching) {
      return;
    }

    if (this.rightItem && this.matchedItems.length > 0) {
      this.readyToMatch.emit({
        rightItem: this.rightItem,
        matchedItems: [...this.matchedItems]
      });
    } else {
      this.readyToMatch.emit({
        rightItem: undefined,
        matchedItems: undefined
      });
    }
  }

  // Public method to clear matching state - call after successful merge operations
  public clearMatchingState(): void {
    this.matchedItems = [];
    this.rightItem = null;
    this.readyToMatch.emit({
      rightItem: undefined,
      matchedItems: undefined
    });
  }

  // Get CSS classes for a row based on its state
  getRowClasses(item: T): string {
    const classes: string[] = [];

    if (this.enableMatching) {
      const state = this.getItemState(item);
      if (state === 'matched') {
        classes.push('matched-row');
      } else if (state === 'right') {
        classes.push('right-row');
      }
    }

    if (this.rowHighlightPredicate?.(item)) {
      classes.push('row-highlighted');
    }

    return classes.join(' ');
  }

  // Resolves the leading-edge accent colour for a row, or null when none applies.
  getRowAccent(item: T): string | null {
    return this.rowAccentFn?.(item) ?? null;
  }
}
